/**
 * Panel de super_admin para revisar y validar las 60 equivalencias texto→clave
 * de ACTIVIDAD_ECONOMICA (RIPS F36).
 *
 * Vive dentro de `ConfiguracionNormativa.jsx` → tab "Catálogos CNBV".
 *
 * Flujo:
 *  1. Al abrir, siembra las equivalencias faltantes (idempotente; nunca sobrescribe validadas).
 *  2. Lista con badge "Pendiente" en amber para las no validadas.
 *  3. Cada fila expone buscador que consulta /catalogos-rips/buscar?catalogo=ACTIVIDAD_ECONOMICA.
 *  4. Al elegir clave, PUT /catalogos-rips/equivalencias/{id} marca `validada=True`.
 */
import { useEffect, useState } from "react";
import { RefreshCw, CheckCircle2, AlertTriangle, Search, PenLine, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function EquivalenciasActividadPanel() {
  const [equivalencias, setEquivalencias] = useState([]);
  const [pendientes, setPendientes] = useState(0);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [sembrando, setSembrando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [filtro, setFiltro] = useState("todos"); // todos / pendientes / validadas

  const cargar = async () => {
    setCargando(true);
    try {
      const { data } = await api.get("/catalogos-rips/equivalencias");
      setEquivalencias(data.equivalencias || []);
      setPendientes(data.pendientes || 0);
      setTotal(data.total || 0);
    } catch (err) {
      // Silencioso: rol no autenticado o error de red.
    } finally {
      setCargando(false);
    }
  };

  const sembrar = async () => {
    setSembrando(true);
    try {
      const { data } = await api.post("/catalogos-rips/equivalencias/seed");
      toast.success(`Equivalencias sembradas: ${data.creados} propuestas, ${data.preservados_validados} validadas preservadas`);
      await cargar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al sembrar equivalencias.");
    } finally {
      setSembrando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const abrirEdicion = (eq) => {
    setEditandoId(eq.id);
    setBusqueda(eq.descripcion_clave || eq.texto);
    setResultados([]);
    setTimeout(() => buscar(eq.descripcion_clave || eq.texto), 100);
  };

  const cerrarEdicion = () => {
    setEditandoId(null);
    setBusqueda("");
    setResultados([]);
  };

  const buscar = async (qOverride) => {
    const q = qOverride ?? busqueda;
    if (!q || q.trim().length < 2) { setResultados([]); return; }
    setBuscando(true);
    try {
      const { data } = await api.get("/catalogos-rips/buscar", {
        params: { catalogo: "ACTIVIDAD_ECONOMICA", q },
      });
      setResultados(data.resultados || []);
    } catch (err) {
      toast.error("Error en la búsqueda.");
    } finally {
      setBuscando(false);
    }
  };

  const asignar = async (eqId, clave) => {
    try {
      await api.put(`/catalogos-rips/equivalencias/${eqId}`, { clave_asignada: clave });
      toast.success("Equivalencia validada.");
      cerrarEdicion();
      await cargar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al validar.");
    }
  };

  const equivalenciasFiltradas = equivalencias.filter((eq) => {
    if (filtro === "pendientes") return !eq.validada;
    if (filtro === "validadas") return eq.validada;
    return true;
  });

  return (
    <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
         data-testid="equivalencias-panel">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h4 className="font-heading text-sm font-semibold text-slate-800">
            Equivalencias: texto legible → clave oficial ACTIVIDAD_ECONOMICA
          </h4>
          <p className="mt-1 max-w-2xl text-xs text-slate-500">
            Cada opción del selector curado del expediente se enlaza con una clave oficial CNBV.
            El texto NO cambia para el usuario final; la clave se guarda como dato adicional.
            Revisa cada propuesta y confirma o cámbiala antes de emitir reportes al SITI.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge variant="outline"
                 className={pendientes > 0
                   ? "border-amber-300 bg-amber-50 text-amber-800"
                   : "border-emerald-300 bg-emerald-50 text-emerald-800"}
                 data-testid="equivalencias-badge-pendientes">
            {pendientes} pendientes · {total - pendientes} validadas
          </Badge>
          <Button variant="outline" size="sm" onClick={sembrar} disabled={sembrando || cargando}
                  data-testid="equivalencias-seed-btn">
            {sembrando ? <RefreshCw className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
            Sembrar/actualizar
          </Button>
        </div>
      </div>

      {/* Aviso permanente sobre asignación manual y riesgo de clave equivocada */}
      <div className="mb-4 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
           data-testid="equivalencias-aviso">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <strong className="block">Aviso importante — asignación manual obligatoria</strong>
          Las equivalencias marcadas como <strong>Pendiente</strong> deben ser asignadas manualmente por el
          super_admin usando el buscador del catálogo oficial CNBV. <strong>Una clave equivocada produce un
          reporte incorrecto ante la CNBV</strong>. Antes de emitir reportes al SITI, revisa que cada opción
          curada apunte a la actividad correcta (fabricación, comercio o servicio) del catálogo oficial.
        </div>
      </div>

      <div className="mb-3 flex gap-2 text-xs">
        {[["todos", "Todos"], ["pendientes", "Solo pendientes"], ["validadas", "Solo validadas"]].map(([k, label]) => (
          <button key={k} onClick={() => setFiltro(k)}
                  data-testid={`equivalencias-filtro-${k}`}
                  className={`rounded px-3 py-1 ${filtro === k ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {label}
          </button>
        ))}
      </div>

      {cargando ? (
        <div className="p-6 text-center text-sm text-slate-400">Cargando…</div>
      ) : equivalenciasFiltradas.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-400"
             data-testid="equivalencias-vacio">
          Sin equivalencias en este filtro. Pulsa &quot;Sembrar/actualizar&quot; para generar propuestas.
        </div>
      ) : (
        <div className="max-h-[600px] overflow-y-auto rounded border border-slate-100">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs">
              <tr>
                <th className="p-2 font-medium text-slate-600">Opción curada</th>
                <th className="p-2 font-medium text-slate-600">Clave asignada</th>
                <th className="p-2 font-medium text-slate-600">Estado</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {equivalenciasFiltradas.map((eq) => (
                <>
                  <tr key={eq.id} className="border-t border-slate-100 align-top"
                      data-testid={`equivalencia-row-${eq.id}`}>
                    <td className="p-2 text-slate-800">
                      {eq.texto}
                    </td>
                    <td className="p-2">
                      {eq.clave_asignada ? (
                        <div>
                          <span className="font-mono text-xs text-slate-700">{eq.clave_asignada}</span>
                          <div className="mt-0.5 text-[11px] text-slate-500">{eq.descripcion_clave}</div>
                          {eq.razon && (
                            <div className="mt-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] italic text-amber-800"
                                 data-testid={`equivalencia-razon-${eq.id}`}>
                              {eq.razon}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs italic text-slate-400">Sin clave</span>
                      )}
                    </td>
                    <td className="p-2">
                      {eq.validada ? (
                        <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px]"
                               data-testid={`equivalencia-validada-${eq.id}`}>
                          <CheckCircle2 className="mr-1 h-3 w-3" /> Validada
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800 text-[10px]"
                               data-testid={`equivalencia-pendiente-${eq.id}`}>
                          <AlertTriangle className="mr-1 h-3 w-3" /> Pendiente
                        </Badge>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      {editandoId === eq.id ? (
                        <Button variant="ghost" size="sm" onClick={cerrarEdicion}
                                data-testid={`equivalencia-cancel-${eq.id}`}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => abrirEdicion(eq)}
                                data-testid={`equivalencia-edit-${eq.id}`}>
                          <PenLine className="mr-1 h-3.5 w-3.5" /> Cambiar
                        </Button>
                      )}
                    </td>
                  </tr>
                  {editandoId === eq.id && (
                    <tr className="bg-slate-50">
                      <td colSpan={4} className="p-3">
                        <div className="flex gap-2">
                          <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                                 onKeyDown={(e) => e.key === "Enter" && buscar()}
                                 placeholder="Busca en el catálogo oficial ACTIVIDAD_ECONOMICA…"
                                 data-testid={`equivalencia-buscar-input-${eq.id}`}
                                 className="flex-1" autoFocus />
                          <Button onClick={() => buscar()} disabled={buscando} size="sm"
                                  data-testid={`equivalencia-buscar-btn-${eq.id}`}>
                            {buscando ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                          </Button>
                        </div>
                        {resultados.length > 0 && (
                          <div className="mt-2 max-h-56 overflow-y-auto rounded border border-slate-200 bg-white">
                            {resultados.map((r) => (
                              <button key={r.clave}
                                      onClick={() => asignar(eq.id, r.clave)}
                                      data-testid={`equivalencia-result-${eq.id}-${r.clave}`}
                                      className="block w-full border-b border-slate-100 px-3 py-2 text-left text-xs hover:bg-emerald-50">
                                <span className="font-mono font-semibold text-slate-800">{r.clave}</span>
                                <span className="ml-2 text-slate-600">{r.descripcion}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
